import { useCallback, useEffect, useRef, useState } from 'react';

import { failedSlot, runCharts } from '../services/charts';

// The wait when a refusal the run asks again after names none: the busy
// server says 5 s; the person's own request still running is a moment.
const DEFAULT_WAIT_S = { inFlight: 2, charts_busy: 5 };

/**
 * A refusal that waiting answers: the person's own chart request still
 * running (A3's per-person share: one in flight, one waiting — 429
 * `budget: 'inFlight'`), or every chart slot of the server taken (503
 * `charts_busy`). Its wait in seconds, or null for anything else — the
 * per-minute charts budget, a refusal of the chart itself.
 */
function waitFor(slot) {
	const error = slot && slot.ok === false ? slot.error || {} : null;
	if (!error) return null;
	const kind = error.code === 'rate_limited' && error.budget === 'inFlight' ? 'inFlight' : (error.code === 'charts_busy' ? 'charts_busy' : null);
	if (!kind) return null;
	const named = typeof error.retryAfter === 'number' && Number.isFinite(error.retryAfter) ? error.retryAfter : null;
	return Math.max(1, Math.ceil(named !== null ? named : DEFAULT_WAIT_S[kind]));
}

/**
 * Runs one saved chart through the batch run (POST /api/charts/run, charts
 * A3) and keeps its answer: the chart page's one request for its numbers.
 *
 *   chartId       the saved chart
 *   collectionId  the CONTEXT: the collection whose items it counts (the
 *                 chart's own, or a sub-collection that inherits it)
 *   range         undefined — the chart's own range; null — all time; or a
 *                 `{ preset }` / `{ from, to }` replacing it for this run
 *                 (the chart page's range control, in the URL)
 *   enabled       false holds the run (no access, nothing to run yet)
 *
 * Returns { result, loading, ranAt, refresh }. `result` is the run's slot —
 * a run result, or a failed slot `{ ok: false, chart_id, error }` — and a
 * refused REQUEST (the charts class's 429, the busy server's 503, no answer)
 * arrives as a failed slot too (services/charts failedSlot), so the chart's
 * frame says it in its own words. While a re-run is out the last answer
 * stays (the frame holds it, dimmed).
 *
 * ONE RUN AT A TIME (the A5 review's M-1). The server lets a person have one
 * chart request in flight and one waiting, and refuses the next (429
 * `inFlight`); a range picked, or Refresh pressed, while a run is out would
 * be that next one. So a run asked while one is out waits, and the newest
 * question is asked when the answer lands; an answer to a question since
 * changed is dropped, never shown. A refusal that waiting answers — the
 * person's own request still running, the server's chart slots all taken —
 * is asked again ONCE, after the wait the server named; a change of chart,
 * context or range, or leaving, drops that retry.
 */
export default function useChartRun({ chartId, collectionId, range, enabled = true }) {
	const [result, setResult] = useState(null);
	const [ranAt, setRanAt] = useState(null);
	const [loading, setLoading] = useState(false);
	const rangeKey = range === undefined ? 'own' : JSON.stringify(range);
	const key = enabled && chartId && collectionId ? `${collectionId}|${chartId}|${rangeKey}` : null;

	const latest = useRef({});
	latest.current = { key, chartId, collectionId, range };
	const flying = useRef(false);
	const queued = useRef(false);
	const retryTimer = useRef(null);
	const alive = useRef(true);
	const runRef = useRef(null);

	const clearRetry = () => {
		if (retryTimer.current) {
			clearTimeout(retryTimer.current.timer);
			retryTimer.current = null;
		}
	};

	const run = useCallback(async ({ retry = false } = {}) => {
		const now = latest.current;
		if (!alive.current || !now.key) return;
		if (flying.current) {
			queued.current = true;
			setLoading(true);
			return;
		}
		clearRetry();
		flying.current = true;
		setLoading(true);
		const sentKey = now.key;
		const request = { chart_id: now.chartId, collection_id: now.collectionId };
		if (now.range !== undefined) request.range = now.range;
		let slot;
		let at = null;
		try {
			const answer = await runCharts([request]);
			slot = answer.results[0] || failedSlot(null, now.chartId);
			at = (slot && slot.ranAt) || answer.ranAt || null;
		} catch (e) {
			slot = failedSlot(e, now.chartId);
		}
		flying.current = false;
		if (!alive.current) return;
		const next = latest.current;
		if (next.key === sentKey) {
			setResult(slot);
			setRanAt(slot && slot.ok !== false ? at : null);
			const wait = retry ? null : waitFor(slot);
			if (wait !== null && !queued.current) {
				retryTimer.current = {
					key: sentKey,
					timer: setTimeout(() => {
						const scheduled = retryTimer.current;
						retryTimer.current = null;
						// Only for the question it was set for.
						if (scheduled && latest.current.key === scheduled.key && runRef.current) runRef.current({ retry: true });
					}, wait * 1000),
				};
			}
		}
		if (queued.current || (next.key && next.key !== sentKey)) {
			queued.current = false;
			if (next.key && runRef.current) {
				runRef.current();
				return;
			}
		}
		setLoading(false);
	}, []);
	runRef.current = run;

	useEffect(() => {
		// A new question: any retry for the last one is dropped.
		clearRetry();
		if (key) run();
	}, [key, run]);

	useEffect(() => {
		// A different chart starts from nothing, never from another chart's answer.
		setResult(null);
		setRanAt(null);
	}, [chartId, collectionId]);

	useEffect(() => {
		alive.current = true;
		return () => {
			alive.current = false;
			clearRetry();
		};
	}, []);

	const refresh = useCallback(() => {
		clearRetry();
		run();
	}, [run]);

	return { result, loading, ranAt, refresh };
}
